<template>
  <v-card class="inquiry-card" elevation="2">
    <!-- 상담신청 완료 상태 -->
    <div v-if="isCompleted" class="completion-state">
      <v-card-title class="completion-header">
        <div class="completion-icon-wrapper">
          <v-icon icon="mdi-check-circle" color="success" size="48"></v-icon>
        </div>
        <h2 class="completion-title">상담신청이 완료되었습니다!</h2>
      </v-card-title>
      
      <v-card-text class="completion-content">
        <div class="completion-message">
          <p class="completion-main-text">
            담당자를 배정하여 빠른 시일 내에 연락드리도록 하겠습니다.
          </p>
          <div class="completion-info">
            <div class="info-item">
              <v-icon icon="mdi-clock-outline" color="primary" size="20" class="me-2"></v-icon>
              <span>영업일 기준 1-2일 내 연락 예정</span>
            </div>
            <div class="info-item">
              <v-icon icon="mdi-phone" color="primary" size="20" class="me-2"></v-icon>
              <span>연락처: 1833-7489</span>
            </div>
          </div>
        </div>
      </v-card-text>
      
      <v-card-actions class="completion-actions">
        <v-btn
          color="primary"
          variant="outlined"
          @click="resetToNewInquiry"
          class="new-inquiry-btn"
          size="large"
          block
        >
          새로운 상담신청
        </v-btn>
      </v-card-actions>
    </div>

    <!-- 상담신청 폼 상태 -->
    <div v-else>
      <!-- 헤더 -->
      <v-card-title class="inquiry-header">
        <h2 class="inquiry-title">상담신청</h2>
      </v-card-title>

      <!-- 폼 내용 -->
      <v-card-text class="inquiry-content">
        <v-form ref="formRef" class="inquiry-form">
          <!-- 기본 정보 -->
          <div class="form-section">
            <h3 class="section-inquiry-title">기본 정보</h3>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.compName"
                  variant="outlined"
                  maxlength="50"
                  hide-details="auto"
                  :rules="[rules.required]"
                  color="primary"
                >
                  <template #label>
                    기관명 <span class="required-asterisk">*</span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.compGubun"
                  variant="outlined"
                  :items="compGubuns"
                  hide-details="auto"
                  :rules="[rules.required]"
                  color="primary"
                >
                  <template #label>
                    사업구분 <span class="required-asterisk">*</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.compSize"
                  label="기관 규모 (직원 수)"
                  variant="outlined"
                  type="number"
                  hide-details="auto"
                  color="primary"
                  placeholder="예: 50명"
                />
              </v-col>
            </v-row>
          </div>

          <!-- 연락처 정보 -->
          <div class="form-section">
            <h3 class="section-inquiry-title">연락처 정보</h3>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  variant="outlined"
                  maxlength="20"
                  hide-details="auto"
                  :rules="[rules.required]"
                  color="primary"
                >
                  <template #label>
                    담당자명 <span class="required-asterisk">*</span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.position"
                  label="직책"
                  variant="outlined"
                  maxlength="20"
                  hide-details="auto"
                  color="primary"
                  placeholder="예: 팀장, 과장"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.phone"
                  variant="outlined"
                  maxlength="13"
                  hide-details="auto"
                  :rules="[rules.required, rules.phone]"
                  color="primary"
                  placeholder="010-0000-0000"
                >
                  <template #label>
                    휴대폰 번호 <span class="required-asterisk">*</span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.email"
                  variant="outlined"
                  maxlength="50"
                  hide-details="auto"
                  :rules="[rules.required, rules.email]"
                  color="primary"
                  placeholder="example@company.com"
                >
                  <template #label>
                    이메일 <span class="required-asterisk">*</span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- 문의 내용 -->
          <div class="form-section">
            <h3 class="section-inquiry-title">문의 내용</h3>
            <v-textarea
              v-model="form.memo"
              variant="outlined"
              rows="4"
              maxlength="500"
              hide-details="auto"
              :rules="[rules.required]"
              color="primary"
              counter
              placeholder="도입을 고려하시는 서비스나 궁금한 점을 자세히 적어주세요."
            >
              <template #label>
                문의사항을 상세히 작성해주세요 <span class="required-asterisk">*</span>
              </template>
            </v-textarea>
          </div>

          <!-- 개인정보처리방침 동의 -->
          <div class="form-section">
            <policyMent @success="formPolicyTrue()" @false="formPolicyFalse()" />
          </div>
        </v-form>
      </v-card-text>

      <!-- 액션 버튼 -->
      <v-card-actions class="inquiry-actions">
        <v-btn
          color="primary"
          variant="elevated"
          @click="registInquiry"
          class="action-btn"
          :loading="loading"
          size="large"
          block
        >
          상담신청
        </v-btn>
      </v-card-actions>

      <!-- 로딩 오버레이 -->
      <v-overlay
        v-model="loading"
        contained
        class="align-center justify-center"
      >
        <v-progress-circular
          color="primary"
          indeterminate
          size="64"
        />
      </v-overlay>
    </div>

    <!-- 확인 다이얼로그 -->
    <v-dialog v-model="confirmDialog" max-width="500px" persistent>
      <v-card class="confirm-dialog-card">
        <v-card-title class="confirm-dialog-header">
          <v-icon icon="mdi-help-circle" color="orange" size="32" class="me-3"></v-icon>
          <span class="confirm-dialog-title">상담신청 확인</span>
        </v-card-title>
        
        <v-card-text class="confirm-dialog-content">
          <div class="confirm-message">
            <p class="confirm-main-text">
              입력하신 정보로 상담신청을 진행하시겠습니까?
            </p>
            <div class="confirm-warning">
              <v-icon icon="mdi-information" color="info" size="20" class="me-2"></v-icon>
              <span class="warning-text">
                입력하신 정보가 유효하지 않을 경우 상담문의가 자동 취소될 수 있습니다.
              </span>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="confirm-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="grey"
            @click="confirmDialog = false"
            class="confirm-btn"
          >
            취소
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="submitInquiry"
            class="confirm-btn"
            :loading="loading"
          >
            상담신청
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useStore } from 'vuex';
import policyMent from './policyMent.vue';
import axios from 'axios';

const store = useStore();

const compGubuns = [
  '요양시설',
  '주간보호센터',
  '재가센터',
  '의료기관',
  '사회복지시설',
  '기타'
];

const form = reactive({
  serviceConsult: true,
  groupPurchase: false,
  compName: '',
  compGubun: null,
  compSize: '',
  name: '',
  position: '',
  phone: '',
  email: '',
  memo: '',
  policy: false
});

// 유효성 검사 규칙
const rules = {
  required: (value: any) => !!value || '필수 입력 항목입니다.',
  email: (value: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value) || '올바른 이메일 형식을 입력해주세요.';
  },
  phone: (value: string) => {
    const pattern = /^01[0-9]-\d{4}-\d{4}$/;
    return pattern.test(value) || '올바른 휴대폰 번호 형식을 입력해주세요. (010-0000-0000)';
  }
};

const loading = ref(false);
const formRef = ref();
const confirmDialog = ref(false);
const isCompleted = ref(false);

const formPolicyTrue = () => {
  form.policy = true;
};

const formPolicyFalse = () => {
  form.policy = false;
};

const resetForm = () => {
  Object.assign(form, {
    serviceConsult: true,
    groupPurchase: false,
    compName: '',
    compGubun: null,
    compSize: '',
    name: '',
    position: '',
    phone: '',
    email: '',
    memo: '',
    policy: false
  });
  if (formRef.value) {
    formRef.value.resetValidation();
  }
};

const resetToNewInquiry = () => {
  isCompleted.value = false;
  resetForm();
};

const registInquiry = async () => {
  // 폼 유효성 검사
  const { valid } = await formRef.value.validate();
  if (!valid) {
    store.commit('showSnackbar', {
      text: "입력 정보를 확인해주세요.",
      color: "error"
    });
    return;
  }

  // 상담 유형 선택 확인
  if (!form.serviceConsult && !form.groupPurchase) {
    store.commit('showSnackbar', {
      text: "상담 유형을 선택해주세요.",
      color: "error"
    });
    return;
  }

  // 개인정보처리방침 동의 확인
  if (!form.policy) {
    store.commit('showSnackbar', {
      text: "개인정보처리방침에 동의해주세요.",
      color: "error"
    });
    return;
  }

  // 확인 다이얼로그 표시
  confirmDialog.value = true;
};

const submitInquiry = async () => {
  try {
    loading.value = true;
    confirmDialog.value = false;
    
    const response = await axios.post('/api/registInquiry', { form: form });
    const result = response.data;
    
    if (result.success === true) {
      // 성공 시 완료 상태로 변경 (스낵바는 표시하지 않음)
      isCompleted.value = true;
    } else {
      store.commit('showSnackbar', {
        text: "상담문의 신청이 불가합니다.",
        color: "error"
      });
    }
  } catch (error) {
    store.commit('showSnackbar', {
      text: "상담문의 신청을 처리할 수 없습니다.",
      color: "error"
    });
    console.error(error);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.inquiry-card {
  max-height: none;
  overflow-y: visible;
}

/* 완료 상태 스타일 */
.completion-state {
  text-align: center;
}

.completion-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.completion-icon-wrapper {
  margin-bottom: 16px;
}

.completion-title {
  font-size: 24px;
  font-weight: 700;
  color: #2e7d32;
  margin: 0;
}

.completion-content {
  padding: 32px 24px;
}

.completion-message {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
}

.completion-main-text {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

.completion-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.completion-actions {
  padding: 16px 24px 32px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.new-inquiry-btn {
  font-weight: 600;
  height: 48px;
}

/* 기존 폼 스타일 */
.inquiry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.inquiry-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.inquiry-content {
  padding: 24px;
}

.inquiry-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section .section-inquiry-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #1976d2;
  text-align: left;
}

.checkbox-group {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.inquiry-actions {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.action-btn {
  min-width: 120px;
  height: 48px;
  font-weight: 600;
}

/* 필수 항목 별표 스타일 */
.required-asterisk {
  color: #e53e3e;
  font-weight: bold;
  margin-left: 2px;
}

/* 확인 다이얼로그 스타일 */
.confirm-dialog-card {
  border-radius: 12px;
}

.confirm-dialog-header {
  display: flex;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.confirm-dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.confirm-dialog-content {
  padding: 24px;
}

.confirm-message {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-main-text {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
  text-align: center;
}

.confirm-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background-color: #e3f2fd;
  border-radius: 8px;
}

.warning-text {
  font-size: 14px;
  color: #1976d2;
  line-height: 1.4;
}

.confirm-dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.confirm-btn {
  min-width: 100px;
  height: 40px;
  font-weight: 500;
}

/* 반응형 디자인 */
@media (max-width: 600px) {
  .completion-header {
    padding: 32px 16px 16px;
  }
  
  .completion-title {
    font-size: 20px;
  }
  
  .completion-content {
    padding: 24px 16px;
  }
  
  .completion-actions {
    padding: 12px 16px 24px;
  }
  
  .completion-info {
    align-items: flex-start;
  }
  
  .info-item {
    justify-content: flex-start;
  }
  
  .inquiry-header {
    padding: 16px;
  }
  
  .inquiry-title {
    font-size: 18px;
  }
  
  .inquiry-content {
    padding: 16px;
  }
  
  .inquiry-actions {
    padding: 12px 16px;
  }
  
  .checkbox-group {
    flex-direction: column;
    gap: 8px;
  }
  
  .section-inquiry-title {
    font-size: 14px;
  }
  
  .confirm-dialog-header {
    padding: 20px 16px 12px;
  }
  
  .confirm-dialog-title {
    font-size: 16px;
  }
  
  .confirm-dialog-content {
    padding: 16px;
  }
  
  .confirm-main-text {
    font-size: 14px;
  }
  
  .confirm-dialog-actions {
    padding: 12px 16px;
  }
}

/* 접근성 개선 */
.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 다크 모드 지원 */
@media (prefers-color-scheme: dark) {
  .completion-header,
  .completion-actions {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  }
  
  .completion-title {
    color: #4ade80;
  }
  
  .completion-main-text {
    color: #e2e8f0;
  }
  
  .info-item {
    color: #94a3b8;
  }
  
  .inquiry-header,
  .inquiry-actions {
    background-color: #2d2d2d;
    border-color: #404040;
  }
  
  .inquiry-title {
    color: #fff;
  }
  
  .section-inquiry-title {
    color: #e0e0e0;
    border-bottom-color: #64b5f6;
  }
  
  
  .confirm-dialog-header,
  .confirm-dialog-actions {
    background-color: #2d2d2d;
    border-color: #404040;
  }
  
  .confirm-dialog-title,
  .confirm-main-text {
    color: #fff;
  }
  
  .warning-text {
    color: #93c5fd;
  }
}
</style> 